<template>
  <ThreeColumn :breadcrumbPaths="paths">
    <template v-slot:title>
      <h1 class="text-h1 mb-0">{{$t('search.advs.advanced')}}</h1>
    </template>

    <template v-slot:mainPanel>
      <v-row no-gutters>
        <!--Search Button-->
        <!--make disabled when none of the inputs are filled-->
        <v-col cols="10" sm="8" md="6" class="pl-0">
          <v-btn 
            @click="go_results" 
            large block
            class="mb-4"
            color="primary"
            :disabled="is_empty_form"
          >
            <v-icon left class="mr-4">mdi-text-search</v-icon>{{$tc('btntxt.use')}}{{$tc('search.advs.advanced')}}
          </v-btn>
        </v-col>
        <v-col cols="10" sm="4" lg="2" :class="$vuetify.breakpoint.xsOnly?'pl-0':'pl-4'">
          <v-btn 
            @click="reset_form" 
            large text 
            :disabled="is_empty_form"
            class="mb-4"
          >
            <v-icon left>mdi-close</v-icon>{{$tc('btntxt.reset_form')}}
          </v-btn>
        </v-col>
      </v-row>

      <!--Keyword Specific Field-->
      <h4 class="wdgt-title mt-5 mb-2 ">{{$tc('search.advs.specfield')}}</h4>
      <v-col class="pl-4 mb-4">
        <!--Citation search-->
        <v-combobox
          :items="citation_suggestions"
          :search-input.sync="citation"
          :value="citation"
          :return-object="false"
          class="mb-2"
          hint="e.g.1: [2002] HKCFI 1234; e.g.2: CACV 154/1984"
          :label="$i18n.t('search.advs.citation')"
          hide-no-data persistent-hint dense clearable filled 
          background-color="lsidebg" 
          append-icon=""
          @change="isSelecting = false"
          @update:list-index="(i) => {isSelecting = (i >= 0)}"
          @keydown.enter="go_results"
          @update:search-input="isSelecting = false"
        ></v-combobox>

        <!--Title search-->
        <v-combobox
          :items="title_suggestions"
          :search-input.sync="title"
          :value="title"
          :return-object="false"
          class="mb-2"
          hint="e.g.1: HKSAR v. CHAN KAM WAH; e.g.2: 香港特別行政區 訴 富士達香港有限公司"
          :label="$i18n.t('database.casename')"
          hide-no-data persistent-hint dense clearable filled 
          background-color="lsidebg" 
          append-icon=""
          @change="isSelecting = false"
          @update:list-index="(i) => {isSelecting = (i >= 0)}"
          @keydown.enter="go_results"
          @update:search-input="isSelecting = false"
        ></v-combobox>

        <!--Legis Name Search-->
        <v-combobox
          :items="captitle_suggestions"
          :search-input.sync="captitle"
          :value="captitle"
          :return-object="false"
          class="mb-2"
          hint="e.g.1: JUSTICES OF THE PEACE ORDINANCE; e.g.2: 建築物能源效益"
          :label="$i18n.t('search.advs.legisname')"
          hide-no-data persistent-hint dense clearable filled 
          background-color="lsidebg" 
          append-icon=""
          @change="isSelecting = false"
          @update:list-index="(i) => {isSelecting = (i >= 0)}"
          @keydown.enter="go_results"
          @update:search-input="isSelecting = false"
        ></v-combobox>

        <!--Party Search-->
        <v-combobox
          :items="parties_suggestions"
          :search-input.sync="parties"
          :value="parties"
          :return-object="false"
          class="align-center"
          hint="e.g.: B & Q PLC"
          :label="$i18n.t('search.advs.parties')"
          hide-no-data persistent-hint dense clearable filled 
          background-color="lsidebg" 
          append-icon=""
          @change="isSelecting = false"
          @update:list-index="(i) => {isSelecting = (i >= 0)}"
          @keydown.enter="go_results"
          @update:search-input="isSelecting = false"
        ></v-combobox>

        <!--Coram Search-->
        <v-combobox
          :items="coram_suggestions"
          :search-input.sync="coram"
          :value="coram"
          :return-object="false"
          class="align-center"
          hint="e.g.1: E.C. Barnes, D.J.; e.g.2: 張慧玲"
          :label="$i18n.t('search.advs.coram')"
          hide-no-data persistent-hint dense clearable filled 
          background-color="lsidebg" 
          append-icon=""
          @change="isSelecting = false"
          @update:list-index="(i) => {isSelecting = (i >= 0)}"
          @keydown.enter="go_results"
          @update:search-input="isSelecting = false"
        ></v-combobox>

        <!--Representation Search-->
        <v-combobox
          :items="representation_suggestions"
          :search-input.sync="representation"
          :value="representation"
          :return-object="false"
          class="align-center"
          hint="e.g.1: G. Alderdice; e.g.2: 資深大律師"
          :label="$i18n.t('search.advs.representation')"
          hide-no-data persistent-hint dense clearable filled 
          background-color="lsidebg" 
          append-icon=""
          @change="isSelecting = false"
          @update:list-index="(i) => {isSelecting = (i >= 0)}"
          @keydown.enter="go_results"
          @update:search-input="isSelecting = false"
        ></v-combobox>

        <!--Charge-->
        <v-combobox 
          :items="charge_suggestions"
          :search-input.sync="charge"
          :value="charge"
          :return-object="false"
          hint="e.g.1: Dangerous driving; e.g.2: 危險駕駛"
          :label="$i18n.t('search.advs.charge')"
          hide-no-data persistent-hint dense clearable filled
          background-color="lsidebg" 
          append-icon=""
          @change="isSelecting = false"
          @update:list-index="(i) => {isSelecting = (i >= 0)}"
          @keydown.enter="go_results"
          @update:search-input="isSelecting = false"
        ></v-combobox>

      </v-col>

      <!--Keyword All Field-->
      <h4 class="wdgt-title mt-8 mb-2">{{$tc('search.advs.allfield')}}</h4>
      <v-col class="pl-4 mb-4">
        <!--All words-->
        <v-text-field hint="e.g. breach fiduciary duty"
          :label="$i18n.t('search.advs.allwords')"
          v-model="text"
          :value="text"
          hide-no-data persistent-hint dense clearable filled
          background-color="lsidebg" 
          @keydown.enter="go_results"
        ></v-text-field>
                
        <!--Any words-->
        <v-text-field 
          hint="e.g. waste pollution radiation"
          :label="$i18n.t('search.advs.anywords')" 
          v-model="anyword"
          :value="anyword"
          hide-no-data persistent-hint dense clearable filled
          background-color="lsidebg" 
          @keydown.enter="go_results"
          ></v-text-field>

        <!--exact phrase-->
        <v-text-field 
          hint="e.g. parliamentary sovereignty"
          :label="$i18n.t('search.advs.phrase')"
          v-model="phrase"
          :value="phrase"
          hide-no-data persistent-hint dense clearable filled
          background-color="lsidebg" 
          @keydown.enter="go_results"
        ></v-text-field>
      </v-col>

      <!--Date search-->
      <h4 class="wdgt-title mt-8 mb-2">{{$tc('search.advs.date')}}</h4>
      <v-row class="pl-4 mb-4">
        <v-col cols="12" sm="6">
          <v-menu
            v-model="menu"
            :close-on-content-click="false"
            :nudge-right="40"
            transition="scale-transition"
            offset-y
            offset-overflow
            max-width="300"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="min_date_text"
                :label="$i18n.t('database.startdate')"
                v-bind="attrs"
                v-on="on"
                hint="DD/MM/YYYY format"
                hide-details="auto"
                hide-no-data persistent-hint dense clearable filled
                background-color="lsidebg" 
                @blur="min_date=parse_date(min_date_text); min_date_text=format_date(min_date)"
                color="primary"
                prepend-icon="mdi-calendar"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="min_date"
              no-title
              min="1800-01-01"
              :max="max_date"
              @input="menu=false; min_date_text=format_date(min_date)"
              full-width
            ></v-date-picker>
          </v-menu>
        </v-col>

        <v-col cols="12" sm="6">
          <v-menu
            v-model="menu2"
            :close-on-content-click="false"
            :nudge-right="40"
            transition="scale-transition"
            offset-y
            offset-overflow
            max-width="300"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="max_date_text"
                :label="$i18n.t('database.enddate')"
                v-bind="attrs"
                v-on="on"
                hint="DD/MM/YYYY format"
                hide-no-data persistent-hint dense clearable filled
                background-color="lsidebg" 
                @blur="max_date=parse_date(max_date_text); max_date_text=format_date(max_date)"
                
                color="primary"
                prepend-icon="mdi-calendar"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="max_date"
              no-title
              :min="min_date"
              :max="today"
              @input="menu2=false; max_date_text=format_date(max_date)"
              full-width
            ></v-date-picker>
          </v-menu>
        </v-col>
      </v-row>

      <!--DB selection-->
      <h4 class="wdgt-title mt-8 mb-2">{{$tc('search.advs.dbs')}}</h4>
      <v-col :class="$vuetify.breakpoint.xs? 'px-0':'pl-4'">
        <v-treeview
          v-model="selectedDbs"
          :value="selectedDbs"
          :items="dbs"
          item-key="name"
          selectable
          return-object
          selected-color="link"
          expand-icon="mdi-chevron-down"
          dense open-on-click
          class="mb-2"
        ></v-treeview>

        <v-row  class="px-3 mb-6 d-flex align-items">
          <span class="mr-4"> {{selectedDbs.length}} {{$tc('search.advs.selected')}} </span>
          <v-spacer></v-spacer>
          <v-btn @click="selectedDbs=[]" text small :disabled="selectedDbs.length==0">
            <v-icon left>mdi-checkbox-blank-off-outline</v-icon>   {{$tc('btntxt.deselect')}}
          </v-btn>
        </v-row>
      </v-col>

      <v-row no-gutters>
        <!--Search Button-->
        <!--make disabled when none of the inputs are filled-->
        <v-col cols="10" sm="8" md="6" class="pl-0">
          <v-btn 
            @click="go_results" 
            large block
            class="mb-4"
            color="primary"
            :disabled="is_empty_form"
          >
          <v-icon left class="mr-4">mdi-text-search</v-icon>{{$tc('btntxt.use')}}{{$tc('search.advs.advanced')}}
          </v-btn>
        </v-col>
        <v-col  cols="10" sm="4" lg="2" :class="$vuetify.breakpoint.xsOnly?'pl-0':'pl-4'">
          <v-btn 
            @click="reset_form" 
            large text 
            :disabled="is_empty_form"
            class="mb-4"
          >
          <v-icon left>mdi-close</v-icon>{{$tc('btntxt.reset_form')}}
          </v-btn>
        </v-col>
      </v-row>
    </template>

    <template v-slot:rightPanel>
      <!--operator button-->
      <!--replace with widget-->
      <v-card flat class="rsidecard mb-4 mr-3 pb-3" max-width="400" width="100%" min-width="250">
        <v-card-title class="px-5">
          <h5 class="wdgt-title" >{{$t('search.advs.help')}}</h5>
        </v-card-title>
        <v-card-actions class="px-5">
        <v-btn color="navlink" outlined block :to="{name:'operators'}" >{{$t('operators.t_operators')}}</v-btn>
        <!--change to popup at side bar-->
        </v-card-actions>
      </v-card>
    </template>
  </ThreeColumn>
</template>

<script>
  import ThreeColumn from '@/components/applayout/ThreeColumn'
  import charges from '../data/charge.json'
  import api from '@/scripts/api'
  import dbs_simplified from '../data/dbs_simplified.json'
  import datehelper from '@/scripts/datehelper'
  export default {
    name: 'AdvancedSearch',
    data(){
      return {
        text:'',
        title:'',
        captitle:'',
        parties:'',
        coram:'',
        representation:'',
        charge:'',
        anyword:'',
        phrase:'',
        citation:'',
        deselect:false,
        charges:charges,
        title_suggestions:[],
        captitle_suggestions:[],
        parties_suggestions:[],
        coram_suggestions:[],
        representation_suggestions:[],
        charge_suggestions:[],
        citation_suggestions:[],
        selectedDbs:[],
        // sort:'relevance',
        // sorts: ['relevance', 'date','title'],
        is_selecting: false,
        fields: [
            {value:'title', text:'Title'},
            {value:'captitle', text:'Legislation Name'},
            {value:'parties', text:'Parties'},
            {value:'coram', text:'Coram'},
            {value:'charge', text:'Charge'},
            {value:'representation', text:'Representation'},
            {value:'none', text:'None'},
        ],
        min_date:null,
        max_date:null,
        min_date_text:'',
        max_date_text:'',
        today: datehelper.today_str(),
        menu: false,
        menu2:false,   
        if_chinese:true,
        if_english:true,
        if_case:true,
        if_legis:true,
        if_other:true,
      }
    },
    components:{
      ThreeColumn
    },
    computed:{
      paths(){
        return [
          {text: 'HKLII', disabled: false, href: {name: 'home'}},
          {text: this.$t('search.advs.advanced'), disabled: true}
        ]
      },        
      ch_variant(){
        return this.$i18n.locale == 'sc' ? 'sc' : 'tc'
      },
      route_query(){
        var query = {"searchType": "advanced"}
        if(this.text){
          query['text'] = this.text
        }
        if(this.anyword){
          query['anyword'] = this.anyword
        }
        if(this.title){
          query['title'] = this.title
        }
        if(this.citation){
          query['citation'] = this.citation
        }
        if(this.captitle){
          query['captitle'] = this.captitle
        }
        if(this.coram){
          query['coram'] = this.coram
        }
        if(this.charge){
          query['charge'] = this.charge
        }
        if(this.representation){
          query['representation'] = this.representation
        }
        if(this.parties){
          query['parties'] = this.parties
        }
          if(this.phrase){
          query['phrase'] = this.phrase
        }
        if(this.charge){
          query['charge'] = this.charge
        }
        if(this.selectedDbs.length > 0){
          query['dbs'] = this.selectedDbs.map(function (i) {
            return i["id"]
          }).join(',')
        }
        if(this.sort && this.sort !='relevance'){
          query['sortby'] = this.sort
        }
        if(this.min_date_text){
          query['min_date'] = this.min_date_text
        }
        if(this.max_date_text){
          query['max_date'] = this.max_date_text
        }
        return query
      },
      en_case_dbs(){
        return {
          name: 'English Case Databases',
          children: dbs_simplified.filter(
            db => db.path.startsWith('/en/cases/')
          ).map(db => {return {name: db.name, id: db.id, path: db.path}})
        }
      },
      en_legis_dbs(){
        return {
          name:'English Legislation Databases',
          children: dbs_simplified.filter(
            db => db.path.startsWith('/en/legis/')
          ).map(db => {return {name: db.name, id: db.id, path: db.path}})
        }
      },
      en_other_dbs(){
        return {
          name:'English Other Databases',
          children: dbs_simplified.filter(
            db => db.path.startsWith('/en/other/')
          ).map(db => {return {name: db.name, id: db.id, path: db.path}})
        }
      },
      ch_case_dbs(){
        var filtered_dbs = dbs_simplified.filter(db => db.path.startsWith(`/${this.ch_variant}/cases/`))
        if (this.ch_variant == 'sc'){
          // check dbs which only tc variants exists, and add back to the dbs list
          var tc_only = dbs_simplified.filter(
            db => db.path.startsWith('/tc/cases/') && !filtered_dbs.map(db2 => db2.abbr).includes(db.abbr)
          )
          filtered_dbs = filtered_dbs.concat(tc_only)
        }
        return {
          name: this.ch_variant == 'tc' ? '中文判案書資料庫' : '中文判案书资料库',
          children: filtered_dbs.map(db => {return {name: db.name, id: db.id, path: db.path}})
        }
      },
      ch_legis_dbs(){
        var filtered_dbs = dbs_simplified.filter(db => db.path.startsWith(`/${this.ch_variant}/legis/`))
        if (this.ch_variant == 'sc'){
          // check dbs which only tc variants exists, and add back to the dbs list
          var tc_only = dbs_simplified.filter(
            db => db.path.startsWith('/tc/legis/') && !filtered_dbs.map(db2 => db2.abbr).includes(db.abbr)
          )
          filtered_dbs = filtered_dbs.concat(tc_only)
        }
        return {
          name: this.ch_variant == 'tc' ? '中文法例資料庫' : '中文法例资料库',
          children: filtered_dbs.map(db => {return {name: db.name, id: db.id, path: db.path}})
        }
      },
      ch_other_dbs(){
        var filtered_dbs = dbs_simplified.filter(db => db.path.startsWith(`/${this.ch_variant}/other/`))
        if (this.ch_variant == 'sc'){
          // check dbs which only tc variants exists, and add back to the dbs list
          var tc_only = dbs_simplified.filter(
            db => db.path.startsWith('/tc/other/') && !filtered_dbs.map(db2 => db2.abbr).includes(db.abbr)
          )
          filtered_dbs = filtered_dbs.concat(tc_only)
        }
        return {
          name: this.ch_variant == 'tc' ? '其他中文資料庫': '其他中文资料库',
          children: filtered_dbs.map(db => {return {name: db.name, id: db.id, path: db.path}})
        }
      },
      dbs(){
        var dbs = []
        if (this.if_english){
          if(this.if_case){dbs.push(this.en_case_dbs)}
          if(this.if_legis){dbs.push(this.en_legis_dbs)}
          if(this.if_other){dbs.push(this.en_other_dbs)}
        }
        if (this.if_chinese){
          if(this.if_case){dbs.push(this.ch_case_dbs)}
          if(this.if_legis){dbs.push(this.ch_legis_dbs)}
          if(this.if_other){dbs.push(this.ch_other_dbs)}
        }
        return dbs
      },
      is_empty_form(){
        var query_keys = Object.keys(this.route_query)
        const filters = ['searchType', 'dbs', 'min_date', 'max_date']  // dont consider these fields
        var num_query = query_keys.filter(k => !filters.includes(k)).length
        return num_query == 0
      }
    },
    watch:{
      /*'$i18n.locale': function(){
        window.document.title = this.$t('search.advs.advanced') + ' | HKLII'  
      },*/
      '$route.query': function(){
        this.load_form()
      },
      citation: async function () {
        if (this.citation){
          this.citation_suggestions = await api.autocomplete('neutral', this.citation)
        }
      },
      title: async function () {
        if (this.title){
          this.title_suggestions = await api.autocomplete('title', this.title)
        }
      },
      captitle: async function () {
        if (this.captitle){
          this.captitle_suggestions = await api.autocomplete('captitle', this.captitle)
        }
      },
      // parties:function () {
      //   if (this.parties){
      //     api.autocomplete('parties', this.parties).then(res =>{
      //       this.parties_suggestions = this.format_suggestion(res)
      //     })
      //   }     
      // },
      // coram:function () {
      //   if (this.coram){
      //     api.autocomplete('coram', this.coram).then(res =>{
      //       this.coram_suggestions = this.format_suggestion(res)
      //     }) 
      //   }
      // },
      // representation:function () {
      //   if (this.representation){
      //     api.autocomplete('representation', this.representation).then(res =>{
      //       this.coram_suggestions = this.format_suggestion(res)
      //     })
      //   }
      // },
      charge: function(){
        if(this.charge){
          var clc = this.charge.toLowerCase()
          this.charge_suggestions = this.format_suggestion(
            charges.filter(
              charge => charge.toLowerCase().includes(clc)
            ).sort((a, b) => {
              return 0 + b.toLowerCase().startsWith(clc) - a.toLowerCase().startsWith(this.charge)
            })
          )
        }
      },
    },
    methods:{
      parse_date: datehelper.parse_date,
      format_date: datehelper.format_date,
      go_results(){
        if(!this.isSelecting && !this.is_empty_form){
          // when user click prev page in search results, the queries are stored in the router
          this.$router.replace({name:"advanced-search", query:this.route_query})  
          this.$router.push({name:"search-results", query:this.route_query})
        }
      },
      reset_form(){
        if (Object.keys(this.$route.query).length > 0){
          this.$router.replace({name:"advanced-search"})
        }
        this.load_form()
      },
      load_form(){
        var record = this.$route.query
        this.citation = record.citation
        this.title = record.title
        this.captitle = record.captitle
        this.parties = record.parties
        this.coram = record.coram
        this.representation = record.representation
        this.charge = record.charge
        this.text = record.text
        this.anyword = record.anyword
        this.phrase = record.phrase
        this.min_date_text = this.format_date(this.parse_date(record.min_date))  // guard against bad date format
        this.max_date_text = this.format_date(this.parse_date(record.max_date))  // guard against bad date format
        if ('dbs' in record){
          this.selectedDbs = dbs_simplified.filter(
            db => record.dbs.split(',').includes(db.id.toString())
          ).map(db => {return {
            id: db.id,
            name: db.name,
            path: db.path
          }})
        } else {
          this.selectedDbs = []
        }
      },
      format_suggestion(res){
        return res.map(r => {
          return {
            text: r,
            value: `"${r}"`
          }
        })
      }
    },
    mounted(){
      //window.document.title = this.$t('search.advs.advanced') + ' | HKLII'
      this.load_form()
    },    
    metaInfo(){
      return{
        title: this.$i18n.t('search.advs.advanced'),
         meta: [
          {
            name: 'description',
            content: "Advanced search options for finding sepcific documents on HKLII.",
          }
        ],
      }
    },
  }
</script>
 
<style scoped>
  /*dropdown overwrite*/
  .theme--light.v-list{
    background: white;
  }
</style>
